import React from 'react'
import { Link } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { LcarsToggle } from '../common/LcarsToggle'
import { NetworkLabel } from '../common/NetworkLabel'
import { GlobalSearch } from '@/components/common/GlobalSearch'
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher'
import { configService } from '@/config'

interface HeaderProps {
  onMenuClick?: () => void
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const appConfig = configService.getAppConfig()

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left section */}
          <div className="flex items-center space-x-4">
            {/* Logo and title */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-2 md:space-x-3 min-w-0">
                <div className="flex items-center justify-center rounded-lg flex-shrink-0">
                  <img
                    src="/fortiblox-32x32.png"
                    alt="Fortiblox Logo"
                    className="h-10 w-10 md:h-8 md:w-8 object-contain"
                  />
                </div>
                <div className="block min-w-0">
                  <h1 className="text-sm md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white truncate leading-tight">
                    {appConfig.name}
                  </h1>
                </div>
              </Link>
              {/* Mobile: Show "powered by Fortiblox" directly under title */}
              <div className="md:hidden absolute top-9 left-14">
                <p className="text-[9px] text-gray-500 dark:text-gray-400 flex items-center gap-0.5 whitespace-nowrap">
                  powered by <a href="https://www.fortiblox.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-80 transition-opacity ml-0.5">
                    <img src="/FortiBlox Light Mode.png" alt="FortiBlox" style={{ height: '36px' }} className="dark:hidden" />
                    <img src="/FortiBlox Dark Mode.png" alt="FortiBlox" style={{ height: '36px' }} className="hidden dark:inline" />
                  </a>
                </p>
              </div>
              {/* Desktop: Show "powered by Fortiblox" to the right */}
              <div className="hidden md:block">
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-0.5">
                  powered by <a href="https://www.fortiblox.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-80 transition-opacity ml-1">
                    <img src="/FortiBlox Light Mode.png" alt="FortiBlox" style={{ height: '84.375px' }} className="dark:hidden" />
                    <img src="/FortiBlox Dark Mode.png" alt="FortiBlox" style={{ height: '84.375px' }} className="hidden dark:inline" />
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Center section - Search */}
          <div className="hidden sm:flex flex-1 max-w-3xl mx-4">
            <GlobalSearch />
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Hamburger Menu Button - Mobile Only */}
            <button
              onClick={onMenuClick}
              className="sm:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Network selector removed (mainnet-only). Reads as an "X1 Mainnet"
                badge: the "X1" is the LCARS easter-egg toggle; the trailing "t"
                of the network label is the hidden mainnet↔testnet switch. */}
            <span className="hidden sm:inline-flex items-center gap-1">
              <LcarsToggle />
              <NetworkLabel />
            </span>

            {/* Additional actions */}
            <div className="hidden sm:flex items-center space-x-2">
              <ThemeSwitcher />
            </div>
          </div>
        </div>

        {/* Mobile Search Section */}
        <div className="sm:hidden pb-3">
          <GlobalSearch />
        </div>
      </div>
    </header>
  )
}
